import { defineStore } from 'pinia';
import moment from 'moment';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type {
    ErosStatisticsStateType,
    ErosStatisticsGettersType,
    ErosStatisticsActionsType,
} from '@/types/stores/ErosStatisticsStoreTypes';
import type { ErosConfigurationType } from '@/types/ErosConfigurationType';
import type { ErosStatisticsFilterType, ErosStatisticsDataType } from '@/types/statistics/ErosStatisticsType';
import type { FilterChipType } from '@/types/statistics/FilterChipType';
import { formatDateRangeChip } from '@/utils/storeUtils';
import { getIntelligentChartColors } from '@/utils/chartColorUtils';
import i18n from '@/plugins/i18n';
import { VDataTableServer } from 'vuetify/components';
import type { LineChartDataType } from '@/types/ChartType';
import type { ErosMetricType } from '@/types/statistics/ErosMetricType';
import type { CustomResponseType } from '@/types/CustomAxiosReponseType';

const createDefaultFilters = (): ErosStatisticsFilterType => ({
    dateRange: {
        start: moment().subtract(7, 'days').startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
    },
    groupedBy: 'day',
    erosConfigurationId: undefined,
});

const createDefaultData = (): ErosStatisticsDataType => ({
    groupedBy: 'day',
    startDate: '',
    endDate: '',
    summary: {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        successRate: 0,
    },
    groups: [],
});

const createDefaultHeaders = (): VDataTableServer['$props']['headers'] => [
    {
        title: 'statistics.eros.table.period',
        key: 'type',
        sortable: true,
    },
    {
        title: 'statistics.eros.table.totalRequests',
        key: 'total_requests',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.eros.table.successfulRequests',
        key: 'successful_requests',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.eros.table.failedRequests',
        key: 'failed_requests',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.eros.table.successRate',
        key: 'success_rate',
        sortable: true,
        align: 'end',
    },
];

export const useErosStatisticsStore = defineStore<
    'erosStatistics',
    ErosStatisticsStateType,
    ErosStatisticsGettersType,
    ErosStatisticsActionsType
>('erosStatistics', {
    state: (): ErosStatisticsStateType => ({
        isLoading: false,
        isErosLoading: false,
        filters: createDefaultFilters(),
        data: createDefaultData(),
        headers: createDefaultHeaders(),
        erosConfigurations: [],
    }),

    getters: {
        getFilteredData: state => state.data || createDefaultData(),
        getTotalRequests: state => state.data?.summary?.totalRequests || 0,
        hasData: state => (state.data?.groups?.length || 0) > 0,
        getFilterChips: (state): FilterChipType[] => {
            const chips: FilterChipType[] = [];
            const { t } = i18n.global;

            chips.push({
                key: 'groupedBy',
                label: `${t('common.groupedBy')}: ${t(`common.grouping.${state.filters.groupedBy}`)}`,
                value: state.filters.groupedBy,
                icon: 'mdi-group',
                removable: false,
            });

            if (state.filters.dateRange?.start && state.filters.dateRange?.end) {
                chips.push(formatDateRangeChip(state.filters.dateRange));
            }

            if (state.filters.erosConfigurationId && state.filters.erosConfigurationId.length > 0) {
                const configNames = state.erosConfigurations
                    .filter(config => state.filters.erosConfigurationId?.includes(config.id))
                    .map(config => config.name)
                    .join(', ');
                chips.push({
                    key: 'erosConfigurationId',
                    label: `${t('statistics.eros.filters.erosConfiguration')}: ${configNames}`,
                    value: state.filters.erosConfigurationId,
                    icon: 'mdi-receipt-text',
                    removable: true,
                });
            }

            return chips;
        },
        getTranslatedHeaders: state => {
            const { t } = i18n.global;

            return (
                state.headers?.map(header => ({
                    ...header,
                    title: header.title ? t(header.title) : header.title,
                })) || []
            );
        },
        getErosConfigurationOptions: (state: ErosStatisticsStateType) => {
            if (!state.erosConfigurations.length) {
                return [];
            }

            return state.erosConfigurations.map(config => ({
                title: config.name,
                value: config.id,
            }));
        },

        getChartData:
            state =>
                (metric: ErosMetricType): LineChartDataType => {
                    if (!state.data?.groups?.length) {
                        return { labels: [], datasets: [] };
                    }

                    const labels = state.data.groups.map(group => group.label);

                    const configMap = new Map<number, { name: string; data: number[] }>();

                    for (const group of state.data.groups) {
                        for (const config of group.data) {
                            if (!configMap.has(config.eros_configuration_id)) {
                                configMap.set(config.eros_configuration_id, {
                                    name: config.eros_configuration_name,
                                    data: [],
                                });
                            }
                            const configData = configMap.get(config.eros_configuration_id)!;
                            configData.data.push(config[metric]);
                        }
                    }

                    const configEntries = Array.from(configMap.entries());
                    const colorPairs = getIntelligentChartColors(configEntries.length);

                    const datasets = configEntries.map(([_id, configInfo], index) => {
                        const colorPair = colorPairs[index];

                        return {
                            label: configInfo.name,
                            data: configInfo.data,
                            borderColor: colorPair.borderColor,
                            backgroundColor: colorPair.backgroundColor,
                            fill: false,
                            tension: 0.1,
                        };
                    });

                    return { labels, datasets };
                },
    },

    actions: {
        async fetchErosConfigurations() {
            this.isErosLoading = true;
            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: '/eros-configurations',
                onSuccess: (response: CustomResponseType<ErosConfigurationType[]>) => {
                    this.erosConfigurations = response.data.items;
                },
            });
            this.isErosLoading = false;
        },

        async fetchData() {
            this.isLoading = true;

            await new Promise(resolve => setTimeout(resolve, 500));

            this.data = {
                groupedBy: this.filters.groupedBy,
                startDate: this.filters.dateRange?.start?.toISOString() || '',
                endDate: this.filters.dateRange?.end?.toISOString() || '',
                summary: {
                    totalRequests: 1250,
                    successfulRequests: 1187,
                    failedRequests: 63,
                    successRate: 94.96,
                },
                groups: [
                    {
                        type: 'day',
                        label: '15.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 45,
                                successful_requests: 42,
                                failed_requests: 3,
                                success_rate: 93.33,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 38,
                                successful_requests: 37,
                                failed_requests: 1,
                                success_rate: 97.37,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '16.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 52,
                                successful_requests: 50,
                                failed_requests: 2,
                                success_rate: 96.15,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 41,
                                successful_requests: 39,
                                failed_requests: 2,
                                success_rate: 95.12,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '17.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 48,
                                successful_requests: 45,
                                failed_requests: 3,
                                success_rate: 93.75,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 43,
                                successful_requests: 42,
                                failed_requests: 1,
                                success_rate: 97.67,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '18.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 55,
                                successful_requests: 52,
                                failed_requests: 3,
                                success_rate: 94.55,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 47,
                                successful_requests: 45,
                                failed_requests: 2,
                                success_rate: 95.74,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '19.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 60,
                                successful_requests: 57,
                                failed_requests: 3,
                                success_rate: 95.0,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 50,
                                successful_requests: 48,
                                failed_requests: 2,
                                success_rate: 96.0,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '20.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 58,
                                successful_requests: 54,
                                failed_requests: 4,
                                success_rate: 93.1,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 44,
                                successful_requests: 43,
                                failed_requests: 1,
                                success_rate: 97.73,
                            },
                        ],
                    },
                    {
                        type: 'day',
                        label: '21.12.2025',
                        data: [
                            {
                                eros_configuration_id: 1,
                                eros_configuration_name: 'Hlavná pokladňa - SK EET',
                                adapter_type: 'slovak_eet',
                                total_requests: 51,
                                successful_requests: 48,
                                failed_requests: 3,
                                success_rate: 94.12,
                            },
                            {
                                eros_configuration_id: 2,
                                eros_configuration_name: 'Pobočka Praha - CZ EET',
                                adapter_type: 'czech_eet',
                                total_requests: 46,
                                successful_requests: 44,
                                failed_requests: 2,
                                success_rate: 95.65,
                            },
                        ],
                    },
                ],
            };

            this.isLoading = false;
        },
        resetFilters() {
            this.filters = createDefaultFilters();
        },

        async removeFilter(filterKey: string) {
            const defaultFilters = createDefaultFilters();

            switch (filterKey) {
            case 'dateRange':
                this.filters.dateRange = defaultFilters.dateRange;
                break;
            case 'groupedBy':
                this.filters.groupedBy = defaultFilters.groupedBy;
                break;
            case 'erosConfigurationId':
                this.filters.erosConfigurationId = undefined;
                break;
            default:
                console.warn(`Unknown filter key: ${filterKey}`);

                return;
            }

            await this.fetchData();
        },
    },
});
